/**
 * Rollen & Rechte verwalten — Admin-Seite des Rollensystems
 * (docs/rollensystem-plan.md §3, Migrationen Nr. 149/150).
 *
 * Lesen direkt aus den Tabellen (RLS: Interne der eigenen Firma, Super-Admin
 * alle), schreiben NUR über die RPCs — die prüfen Admin und Firma und
 * schreiben das Änderungsprotokoll mit.
 *
 * Fehlen die Migrationen, meldet jede Funktion das im Klartext
 * (`MIGRATION_FEHLT`), statt einen Knopf anzubieten, der ins Leere läuft.
 *
 * Ergebnistypen bewusst flach (kein Diskriminanten-Union): ohne
 * strictNullChecks engt TypeScript `if (!r.ok)` nicht ein.
 *
 * VERTRAG für die Admin-Oberfläche — Signaturen nicht ändern, ohne die
 * Aufrufer mitzuziehen.
 */
import { supabase } from '../supabaseClient';
import { istRecht, type RechtSchluessel } from './katalog';

export const MIGRATION_FEHLT = 'Rollen & Rechte sind in dieser Datenbank noch nicht eingerichtet (Migrationen Nr. 149/150 offen).';

export interface RolleZeile {
    companyId: string;
    schluessel: string;
    name: string;
    beschreibung: string;
    reihenfolge: number;
    /** Startrolle — nicht löschbar. */
    system: boolean;
}

export interface RollenStand {
    verfuegbar: boolean;
    /** Gesetzt genau dann, wenn `verfuegbar` falsch ist. */
    grund: string | null;
    companyId: string | null;
    /** Nach `reihenfolge`, dann Name. */
    rollen: RolleZeile[];
    /** Rolle → erlaubte Rechte. Fehlt eine Rolle, hat sie keine Rechte. */
    matrix: Map<string, Set<RechtSchluessel>>;
    /** user_id → Rollenschlüssel dieser Firma. */
    benutzerRollen: Map<string, string[]>;
}

export interface ProtokollZeile {
    id: number;
    am: string;
    von: string | null;
    art: 'recht' | 'benutzer_rolle' | 'rolle_angelegt' | 'rolle_geloescht' | 'einrichtung';
    rolle: string | null;
    recht: string | null;
    userId: string | null;
    wert: boolean | null;
    name: string | null;
}

export interface Ergebnis {
    ok: boolean;
    meldung: string | null;
}

export interface AnlageErgebnis extends Ergebnis {
    /** Schlüssel der neuen Rolle, wenn `ok`. */
    schluessel: string | null;
}

export interface ProtokollErgebnis extends Ergebnis {
    zeilen: ProtokollZeile[];
}

interface DbFehler { code?: string; message?: string }

const KEINE_FIRMA = 'Keine Firma gewählt — Rollen und Rechte gelten je Firma.';
const NAME_FEHLT = 'Bitte einen Namen für die Rolle eingeben.';

/**
 * Migration offen: Funktion fehlt (PGRST202 = im Schema-Cache unbekannt,
 * 42883 = Funktion fehlt) oder Tabelle fehlt (PostgREST liefert je nach
 * Schema-Cache PGRST205 oder 42P01).
 */
export const fehltMigration = (e: DbFehler | null | undefined): boolean =>
    !!e && (e.code === 'PGRST202' || e.code === '42883' || e.code === 'PGRST205' || e.code === '42P01');

/** supabase-js meldet Fehler im Ergebnis; geworfen wird nur bei Netz- oder Programmfehlern. */
const alsFehler = (e: unknown): DbFehler => ({ message: e instanceof Error ? e.message : String(e ?? '') });

const personen = (n: number): string => (n === 1 ? 'eine Person' : `${n} Personen`);

/** Die Meldungen der RPCs (`<funktion>: …`) in Sätze für den Admin-Reiter. */
export const rollenFehlerText = (e: DbFehler | null | undefined): string => {
    if (fehltMigration(e)) return MIGRATION_FEHLT;
    const m = e?.message || '';
    if (/nur Administratoren/.test(m)) return 'Nur Administratoren dürfen Rollen und Rechte ändern.';
    if (/keine Anmeldung/.test(m)) return 'Keine gültige Anmeldung — bitte neu anmelden.';
    if (/andere Firma/.test(m)) return 'Rolle oder Person gehört zu einer anderen Firma.';
    if (/Rolle nicht gefunden/.test(m)) return 'Diese Rolle gibt es nicht mehr — bitte die Ansicht neu laden.';
    if (/unbekanntes Recht/.test(m)) return 'Dieses Recht kennt die Datenbank nicht — App und Datenbank sind nicht auf demselben Stand.';
    if (/Startrollen lassen sich nicht/.test(m)) return 'Startrollen lassen sich nicht löschen.';
    if (/Freelancer bekommen keine Rollen/.test(m)) return 'Freelancer bekommen keine Rollen.';
    const traeger = /von (\d+) Person/.exec(m);
    if (traeger) return `Die Rolle trägt noch ${personen(Number(traeger[1]))} — erst dort entfernen, dann löschen.`;
    if (e?.code === '55006') return 'Die Rolle wird noch getragen — erst bei allen Personen entfernen, dann löschen.';
    if (e?.code === '23505') return 'Eine Rolle mit diesem Namen gibt es schon.';
    if (e?.code === '42501') return 'Für diese Änderung fehlt die Berechtigung.';
    // Unbekannte Meldung ohne den Funktionsnamen davor — der sagt dem Admin nichts.
    return m.replace(/^[a-z_]+:\s*/, '') || 'Unbekannter Fehler.';
};

const leererStand = (companyId: string | null, grund: string): RollenStand => ({
    verfuegbar: false, grund, companyId, rollen: [], matrix: new Map(), benutzerRollen: new Map(),
});

type RolleRoh = { company_id: string; schluessel: string; name: string | null; beschreibung: string | null; reihenfolge: number | null; system: boolean | null };
type RechtRoh = { rolle: string; recht: string };
type TraegerRoh = { user_id: string; rolle: string };

/** Stand einer Firma: Rollen, Matrix, Rollen je Person. */
export async function ladeRollenStand(companyId: string): Promise<RollenStand> {
    if (!companyId) return leererStand(null, KEINE_FIRMA);
    try {
        const [rollenA, rechteA, traegerA] = await Promise.all([
            supabase.from('rollen')
                .select('company_id, schluessel, name, beschreibung, reihenfolge, system')
                .eq('company_id', companyId),
            supabase.from('rollen_rechte')
                .select('rolle, recht')
                .eq('company_id', companyId)
                .eq('erlaubt', true),
            supabase.from('benutzer_rollen')
                .select('user_id, rolle')
                .eq('company_id', companyId),
        ]);
        const fehler = [rollenA.error, rechteA.error, traegerA.error].filter(Boolean) as DbFehler[];
        // Fehlt nur eine der Tabellen, ist das trotzdem „Migration offen“ und kein Rechtefehler.
        if (fehler.length) return leererStand(companyId, rollenFehlerText(fehler.find(fehltMigration) ?? fehler[0]));

        const rollen: RolleZeile[] = ((rollenA.data ?? []) as RolleRoh[])
            .map(z => ({
                companyId: z.company_id,
                schluessel: z.schluessel,
                name: z.name || z.schluessel,
                beschreibung: z.beschreibung ?? '',
                reihenfolge: Number(z.reihenfolge) || 0,
                system: z.system === true,
            }))
            .sort((a, b) => a.reihenfolge - b.reihenfolge || a.name.localeCompare(b.name, 'de'));

        const matrix = new Map<string, Set<RechtSchluessel>>(rollen.map(r => [r.schluessel, new Set<RechtSchluessel>()]));
        for (const z of (rechteA.data ?? []) as RechtRoh[]) {
            // Ein Recht, das dieser Client nicht kennt (neuere Datenbank), hat
            // hier keine Zeile und prüft keinen Code — es bleibt draußen.
            if (!istRecht(z.recht)) continue;
            const menge = matrix.get(z.rolle) ?? new Set<RechtSchluessel>();
            menge.add(z.recht);
            matrix.set(z.rolle, menge);
        }

        // Chips je Person in derselben Reihenfolge wie die Spalten der Matrix.
        const rang = new Map(rollen.map((r, i) => [r.schluessel, i]));
        const benutzerRollen = new Map<string, string[]>();
        for (const z of (traegerA.data ?? []) as TraegerRoh[]) {
            const liste = benutzerRollen.get(z.user_id) ?? [];
            if (!liste.includes(z.rolle)) liste.push(z.rolle);
            benutzerRollen.set(z.user_id, liste);
        }
        for (const liste of benutzerRollen.values()) {
            liste.sort((a, b) => (rang.get(a) ?? rollen.length) - (rang.get(b) ?? rollen.length) || a.localeCompare(b));
        }

        return { verfuegbar: true, grund: null, companyId, rollen, matrix, benutzerRollen };
    } catch (e) {
        return leererStand(companyId, rollenFehlerText(alsFehler(e)));
    }
}

/** Einheitlich für die Schreib-RPCs ohne Rückgabewert. */
async function schreibe(funktion: string, parameter: Record<string, unknown>): Promise<Ergebnis> {
    try {
        const { error } = await supabase.rpc(funktion, parameter);
        if (error) return { ok: false, meldung: rollenFehlerText(error) };
        return { ok: true, meldung: null };
    } catch (e) {
        return { ok: false, meldung: rollenFehlerText(alsFehler(e)) };
    }
}

/** Matrix-Häkchen. RPC `rolle_recht_setzen(p_company, p_rolle, p_recht, p_erlaubt)`. */
export async function setzeRecht(companyId: string, rolle: string, recht: RechtSchluessel, erlaubt: boolean): Promise<Ergebnis> {
    return schreibe('rolle_recht_setzen', { p_company: companyId, p_rolle: rolle, p_recht: recht, p_erlaubt: erlaubt });
}

/** Rolle einer Person geben/nehmen. RPC `benutzer_rolle_setzen(p_user, p_rolle, p_an)`. */
export async function setzeBenutzerRolle(userId: string, rolle: string, an: boolean): Promise<Ergebnis> {
    return schreibe('benutzer_rolle_setzen', { p_user: userId, p_rolle: rolle, p_an: an });
}

/** Eigene Rolle anlegen. RPC `rolle_anlegen(p_company, p_name, p_beschreibung)` → Schlüssel. */
export async function legeRolleAn(companyId: string, name: string, beschreibung: string): Promise<AnlageErgebnis> {
    const n = (name ?? '').trim();
    if (!n) return { ok: false, meldung: NAME_FEHLT, schluessel: null };
    try {
        const { data, error } = await supabase.rpc('rolle_anlegen', {
            p_company: companyId, p_name: n, p_beschreibung: (beschreibung ?? '').trim(),
        });
        if (error) return { ok: false, meldung: rollenFehlerText(error), schluessel: null };
        if (typeof data !== 'string' || !data) {
            return { ok: false, meldung: 'Die Rolle wurde angelegt, aber ohne Schlüssel zurückgemeldet — bitte die Ansicht neu laden.', schluessel: null };
        }
        return { ok: true, meldung: null, schluessel: data };
    } catch (e) {
        return { ok: false, meldung: rollenFehlerText(alsFehler(e)), schluessel: null };
    }
}

/** Eigene Rolle löschen (nur ohne Träger, nie Startrollen). RPC `rolle_loeschen(p_company, p_rolle)`. */
export async function loescheRolle(companyId: string, rolle: string): Promise<Ergebnis> {
    return schreibe('rolle_loeschen', { p_company: companyId, p_rolle: rolle });
}

const ARTEN: ReadonlySet<string> = new Set<ProtokollZeile['art']>(['recht', 'benutzer_rolle', 'rolle_angelegt', 'rolle_geloescht', 'einrichtung']);

type ProtokollRoh = {
    id: number | string; am: string; von: string | null; art: string; rolle: string | null;
    recht: string | null; user_id: string | null; wert: boolean | null; name: string | null;
};

/** Jüngste Änderungen zuerst. Tabelle `rollen_rechte_protokoll` (nur Admins). */
export async function ladeProtokoll(companyId: string, limit = 100): Promise<ProtokollErgebnis> {
    if (!companyId) return { ok: false, meldung: KEINE_FIRMA, zeilen: [] };
    const anzahl = Number.isFinite(limit) && limit >= 1 ? Math.floor(limit) : 100;
    try {
        const { data, error } = await supabase.from('rollen_rechte_protokoll')
            .select('id, am, von, art, rolle, recht, user_id, wert, name')
            .eq('company_id', companyId)
            .order('am', { ascending: false })
            .order('id', { ascending: false })
            .limit(anzahl);
        if (error) return { ok: false, meldung: rollenFehlerText(error), zeilen: [] };
        const zeilen: ProtokollZeile[] = ((data ?? []) as ProtokollRoh[])
            // Eine Art, die dieser Client nicht kennt (neuere Datenbank), kann
            // die Anzeige nicht beschriften — besser weglassen als falsch zeigen.
            .filter(z => ARTEN.has(z.art))
            .map(z => ({
                id: Number(z.id),
                am: z.am,
                von: z.von ?? null,
                art: z.art as ProtokollZeile['art'],
                rolle: z.rolle ?? null,
                recht: z.recht ?? null,
                userId: z.user_id ?? null,
                wert: typeof z.wert === 'boolean' ? z.wert : null,
                name: z.name ?? null,
            }))
            // PostgREST liefert `am` einheitlich in UTC; die Zeichenfolge sortiert also richtig.
            .sort((a, b) => (b.am ?? '').localeCompare(a.am ?? '') || b.id - a.id);
        return { ok: true, meldung: null, zeilen };
    } catch (e) {
        return { ok: false, meldung: rollenFehlerText(alsFehler(e)), zeilen: [] };
    }
}
